import clsx from 'clsx'
import { rem } from 'polished'
import { useMemo, type CSSProperties } from 'react'
import styled from 'styled-components'

import Caption from 'LuxKit/Typography/Caption'
import Caret from 'components/Common/Caret'
import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'

const ProgressArea = styled(Group)`
  ${mediaQueryUp.tablet} {
    flex: 1 1 0;
    min-width: ${rem(450)};
  }
`

const ProgressColumns = styled(Group)`
  width: 100%;
`

const ProgressColumn = styled(Group)`
  min-width: 0;
`

const TrackSegment = styled.div`
  width: 100%;
  height: ${rem(12)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.surface.lightForeground};

  &.highlight {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
  }
`

const IndicatorStack = styled(Group)`
  align-self: flex-end;
  width: fit-content;
`

const IndicatorCaret = styled(Caret)`
  align-self: flex-end;
  margin-right: ${rem(4)};
  border-width: ${rem(8)};
  border-top-color: ${props => props.theme.palette.neutral.surface.lightForeground};

  &.highlight {
    border-top-color: ${props => props.theme.palette.highlight.primary.normalBackground};
  }
`

const Indicator = styled.div`
  padding: ${rem(4)} ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.M};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};

  &.highlight {
    border-color: ${props => props.theme.palette.highlight.primary.normalBackground};
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
  }
`

const ValueLabel = styled(Caption)`
  text-align: end;
`

interface Props {
  brandName: string
  price: number
  value: number
  pricePercentage: number
  valuePercentage: number
  valueLabel: string
}

function FlashHotelValueComparisonHorizontalBar(props: Props) {
  const { brandName, price, value, pricePercentage, valuePercentage, valueLabel } = props
  const priceColumnStyle = useMemo<CSSProperties>(() => ({
    flexBasis: `${pricePercentage}%`,
  }), [pricePercentage])
  const valueColumnStyle = useMemo<CSSProperties>(() => ({
    flexBasis: `${valuePercentage}%`,
  }), [valuePercentage])

  return (
    <ProgressArea direction="vertical" gap={8}>
      <ProgressColumns direction="horizontal" gap={2}>
        <ProgressColumn direction="vertical" gap={8} style={priceColumnStyle}>
          <IndicatorStack direction="vertical" gap={0} horizontalAlign="center">
            <Indicator className={clsx('highlight')} aria-hidden="true">
              <Caption variant="medium" weight="bold">
                <FormatCurrency value={price} format="roundedDollar" />
              </Caption>
            </Indicator>
            <IndicatorCaret className={clsx('highlight')} />
          </IndicatorStack>
          <TrackSegment className={clsx('highlight')} />
          <Caption variant="medium" weight="bold">
            {brandName}
          </Caption>
        </ProgressColumn>
        <ProgressColumn direction="vertical" gap={8} style={valueColumnStyle}>
          <IndicatorStack direction="vertical" gap={0} horizontalAlign="center">
            <Indicator aria-hidden="true">
              <Caption variant="medium" weight="bold">
                <FormatCurrency value={value} format="roundedDollar" />
              </Caption>
            </Indicator>
            <IndicatorCaret />
          </IndicatorStack>
          <TrackSegment />
          <ValueLabel variant="medium" weight="bold">
            {valueLabel}
          </ValueLabel>
        </ProgressColumn>
      </ProgressColumns>
    </ProgressArea>
  )
}

export default FlashHotelValueComparisonHorizontalBar
